import { DdPage, User } from "@/CodegenTypes";

export type PageDetailsPage = Pick<
  DdPage,
  | "id"
  | "name"
  | "releaseDate"
  | "createdAt"
  | "enteredFrom"
  | "enteredById"
  | "userId"
  | "username"
  | "calculation"
>;

export type PageDetailsUser = Pick<
  User,
  "userId" | "username" | "uplineId" | "lineageIds" | "ddTicketRebate"
> & {
  status?: User["status"];
  downlines?: PageDetailsUser[];
  pages?: PageDetailsPage[];
};

export type PageDetailsRow = PageDetailsPage & {
  upline: string;
  uplineName: string;
  account: string;
  username: string;
  big: number;
  small: number;
  amount: number;
  rebate: number;
  payable: number;
  uplineCount: number;
  accountCount: number;
};

type PageDetailsRowsOptions = {
  includeDownlines?: boolean;
};

function getTime(value: string | Date) {
  return new Date(value).getTime();
}

function getLineageBranchId(rootUserId: string, lineageIds?: string[]) {
  const rootIndex = lineageIds?.indexOf(rootUserId) ?? -1;
  if (rootIndex < 0) {
    return null;
  }
  return lineageIds?.[rootIndex + 1] ?? null;
}

function getImmediateBranchUser(
  rootUser: PageDetailsUser,
  usersById: Map<string, PageDetailsUser>,
  pageUserId: string
) {
  if (pageUserId === rootUser.userId) {
    return rootUser;
  }

  let current = usersById.get(pageUserId);
  const visited = new Set<string>();
  while (current && current.userId !== rootUser.userId) {
    if (current.uplineId === rootUser.userId) {
      return current;
    }
    if (!current.uplineId || visited.has(current.userId)) {
      break;
    }
    visited.add(current.userId);
    current = usersById.get(current.uplineId);
  }

  const lineageBranchId = getLineageBranchId(
    rootUser.userId,
    usersById.get(pageUserId)?.lineageIds
  );
  return (lineageBranchId && usersById.get(lineageBranchId)) || rootUser;
}

export function buildDdPageDetailsRows(
  user?: PageDetailsUser | null,
  options: PageDetailsRowsOptions = {}
) {
  if (!user) {
    return [];
  }

  const includeDownlines = options.includeDownlines ?? false;
  const downlines = user.downlines ?? [];
  const usersById = new Map<string, PageDetailsUser>(
    [user, ...downlines].map((downline) => [downline.userId, downline])
  );
  const pages = includeDownlines
    ? user.pages ?? []
    : (user.pages ?? []).filter((page) => page.userId === user.userId);

  const rows = pages
    .map((page) => {
      const branchUser = getImmediateBranchUser(user, usersById, page.userId);
      const owner = usersById.get(page.userId);
      const big = page.calculation.bigCount;
      const small = page.calculation.smallCount;
      const amount = page.calculation.rawCost;
      const rebate = (amount * branchUser.ddTicketRebate) / 100;
      const payable = (amount * (100 - branchUser.ddTicketRebate)) / 100;

      return {
        ...page,
        upline: branchUser.userId,
        uplineName: branchUser.username,
        account: page.userId,
        username: page.username || owner?.username || "",
        big,
        small,
        amount,
        rebate,
        payable,
      };
    })
    .filter((row) => row.amount > 0)
    .sort((rowA, rowB) => {
      return (
        rowA.upline.localeCompare(rowB.upline) ||
        rowA.account.localeCompare(rowB.account) ||
        getTime(rowA.createdAt) - getTime(rowB.createdAt)
      );
    });

  const uplineCounts = new Map<string, number>();
  const accountCounts = new Map<string, number>();
  for (const row of rows) {
    uplineCounts.set(row.upline, (uplineCounts.get(row.upline) ?? 0) + 1);
    accountCounts.set(row.account, (accountCounts.get(row.account) ?? 0) + 1);
  }

  return rows.map((row) => ({
    ...row,
    uplineCount: uplineCounts.get(row.upline) ?? 0,
    accountCount: accountCounts.get(row.account) ?? 0,
  }));
}
